import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { AdminCompanyRecord, CreateAdminCompanyRequest, UpdateAdminCompanyRequest } from '../../services/api';
import apiService from '../../services/api';

export interface CompaniesState {
    companies: AdminCompanyRecord[];
    selectedCompany: AdminCompanyRecord | null;
    isLoading: boolean;
    error: string | null;
}

const initialState: CompaniesState = {
    companies: [],
    selectedCompany: null,
    isLoading: false,
    error: null,
};

// Async Thunks
export const fetchCompanies = createAsyncThunk(
    'companies/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const companies = await apiService.getAdminCompanies();
            return companies;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch companies');
        }
    }
);

export const createCompany = createAsyncThunk(
    'companies/create',
    async (data: CreateAdminCompanyRequest, { rejectWithValue }) => {
        try {
            const newCompany = await apiService.createAdminCompany(data);
            return newCompany;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to create company');
        }
    }
);

export const fetchCompanyById = createAsyncThunk(
    'companies/fetchById',
    async (id: string, { rejectWithValue }) => {
        try {
            const company = await apiService.getAdminCompanyById(id);
            return company;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch company details');
        }
    }
);

export const updateCompany = createAsyncThunk(
    'companies/update',
    async ({ id, data }: { id: string; data: UpdateAdminCompanyRequest }, { rejectWithValue }) => {
        try {
            const updatedCompany = await apiService.updateAdminCompany(id, data);
            return updatedCompany;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update company');
        }
    }
);

export const uploadCompanyLogo = createAsyncThunk(
    'companies/uploadLogo',
    async ({ id, file }: { id: string; file: File }, { rejectWithValue }) => {
        try {
            const updatedCompany = await apiService.uploadAdminCompanyLogo(id, file);
            return updatedCompany;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to upload logo');
        }
    }
);

export const deleteCompany = createAsyncThunk(
    'companies/delete',
    async (id: string, { rejectWithValue }) => {
        try {
            await apiService.deleteAdminCompany(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete company');
        }
    }
);

const companiesSlice = createSlice({
    name: 'companies',
    initialState,
    reducers: {
        clearCompaniesError: (state) => {
            state.error = null;
        },
        clearSelectedCompany: (state) => {
            state.selectedCompany = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch All
            .addCase(fetchCompanies.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCompanies.fulfilled, (state, action) => {
                state.isLoading = false;
                state.companies = action.payload;
                state.error = null;
            })
            .addCase(fetchCompanies.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Create
            .addCase(createCompany.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createCompany.fulfilled, (state, action) => {
                state.isLoading = false;
                state.companies.unshift(action.payload);
                state.error = null;
            })
            .addCase(createCompany.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch By Id
            .addCase(fetchCompanyById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchCompanyById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedCompany = action.payload;
                state.error = null;
            })
            .addCase(fetchCompanyById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Update
            .addCase(updateCompany.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateCompany.fulfilled, (state, action) => {
                state.isLoading = false;
                const index = state.companies.findIndex(c => c.id === action.payload.id);
                if (index !== -1) {
                    state.companies[index] = action.payload;
                }
                if (state.selectedCompany?.id === action.payload.id) {
                    state.selectedCompany = action.payload;
                }
                state.error = null;
            })
            .addCase(updateCompany.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Delete
            .addCase(deleteCompany.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteCompany.fulfilled, (state, action) => {
                state.isLoading = false;
                state.companies = state.companies.filter(c => c.id !== action.payload);
                if (state.selectedCompany?.id === action.payload) {
                    state.selectedCompany = null;
                }
                state.error = null;
            })
            .addCase(deleteCompany.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Upload Logo
            .addCase(uploadCompanyLogo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(uploadCompanyLogo.fulfilled, (state, action) => {
                state.isLoading = false;
                const index = state.companies.findIndex(c => c.id === action.payload.id);
                if (index !== -1) {
                    state.companies[index] = action.payload;
                }
                if (state.selectedCompany?.id === action.payload.id) {
                    state.selectedCompany = action.payload;
                }
                state.error = null;
            })
            .addCase(uploadCompanyLogo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearCompaniesError, clearSelectedCompany } = companiesSlice.actions;

// Selectors
export const selectCompanies = (state: { companies: CompaniesState }) => state.companies.companies;
export const selectSelectedCompany = (state: { companies: CompaniesState }) => state.companies.selectedCompany;
export const selectCompaniesLoading = (state: { companies: CompaniesState }) => state.companies.isLoading;
export const selectCompaniesError = (state: { companies: CompaniesState }) => state.companies.error;

export default companiesSlice.reducer;
